import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";

function authHeaders(getState) {
  const token = getState().loginToken?.response?.token;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
}

function backupCodesFromPayload(payload) {
  const raw = payload?.data?.backupCodes ?? payload?.backupCodes;
  return Array.isArray(raw) ? raw : [];
}

function setupDataFromPayload(payload) {
  const data = payload?.data ?? payload;
  return {
    qrCodeDataUrl: data?.qrCodeDataUrl ?? "",
    manualEntryKey: data?.manualEntryKey ?? "",
  };
}

export const fetchTwoFactorStatus = createAsyncThunk(
  "twoFactor/fetchStatus",
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${ROOT_URL}/master-user/two-factor`,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const setupTwoFactor = createAsyncThunk(
  "twoFactor/setup",
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/master-user/two-factor/setup`,
        {},
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const cancelTwoFactorSetup = createAsyncThunk(
  "twoFactor/cancelSetup",
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${ROOT_URL}/master-user/two-factor/setup`,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const enableTwoFactor = createAsyncThunk(
  "twoFactor/enable",
  async ({ code }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/master-user/two-factor/enable`,
        { code: String(code || "").trim() },
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const disableTwoFactor = createAsyncThunk(
  "twoFactor/disable",
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/master-user/two-factor/disable`,
        {},
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const regenerateBackupCodes = createAsyncThunk(
  "twoFactor/regenerateBackupCodes",
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/master-user/two-factor/backup-codes/regenerate`,
        {},
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

const initialState = {
  enabled: false,
  hasBackupCodes: false,
  pendingSetup: false,
  statusLoading: false,
  setupLoading: false,
  enableLoading: false,
  disableLoading: false,
  regenerateLoading: false,
  qrCodeDataUrl: "",
  manualEntryKey: "",
  backupCodes: [],
  error: null,
  statusLoaded: false,
};

const twoFactorApi = createSlice({
  name: "twoFactor",
  initialState,
  reducers: {
    clearTwoFactorError: (state) => {
      state.error = null;
    },
    clearTwoFactorSetup: (state) => {
      state.qrCodeDataUrl = "";
      state.manualEntryKey = "";
      state.pendingSetup = false;
    },
    resetTwoFactor: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTwoFactorStatus.pending, (state) => {
        state.statusLoading = true;
        state.error = null;
      })
      .addCase(fetchTwoFactorStatus.fulfilled, (state, action) => {
        state.statusLoading = false;
        state.statusLoaded = true;
        const data = action.payload?.data ?? action.payload;
        state.enabled = Boolean(data?.enabled);
        state.hasBackupCodes = Boolean(data?.hasBackupCodes);
        state.pendingSetup = Boolean(data?.pendingSetup);
      })
      .addCase(fetchTwoFactorStatus.rejected, (state, action) => {
        state.statusLoading = false;
        state.statusLoaded = true;
        state.error = action.payload;
      })
      .addCase(setupTwoFactor.pending, (state) => {
        state.setupLoading = true;
        state.error = null;
      })
      .addCase(setupTwoFactor.fulfilled, (state, action) => {
        state.setupLoading = false;
        state.pendingSetup = true;
        const setup = setupDataFromPayload(action.payload);
        state.qrCodeDataUrl = setup.qrCodeDataUrl;
        state.manualEntryKey = setup.manualEntryKey;
      })
      .addCase(setupTwoFactor.rejected, (state, action) => {
        state.setupLoading = false;
        state.error = action.payload;
      })
      .addCase(cancelTwoFactorSetup.pending, (state) => {
        state.setupLoading = true;
        state.error = null;
      })
      .addCase(cancelTwoFactorSetup.fulfilled, (state) => {
        state.setupLoading = false;
        state.pendingSetup = false;
        state.qrCodeDataUrl = "";
        state.manualEntryKey = "";
      })
      .addCase(cancelTwoFactorSetup.rejected, (state, action) => {
        state.setupLoading = false;
        state.error = action.payload;
      })
      .addCase(enableTwoFactor.pending, (state) => {
        state.enableLoading = true;
        state.error = null;
      })
      .addCase(enableTwoFactor.fulfilled, (state, action) => {
        state.enableLoading = false;
        state.enabled = true;
        state.pendingSetup = false;
        state.hasBackupCodes = true;
        state.qrCodeDataUrl = "";
        state.manualEntryKey = "";
        state.backupCodes = backupCodesFromPayload(action.payload);
      })
      .addCase(enableTwoFactor.rejected, (state, action) => {
        state.enableLoading = false;
        state.error = action.payload;
      })
      .addCase(disableTwoFactor.pending, (state) => {
        state.disableLoading = true;
        state.error = null;
      })
      .addCase(disableTwoFactor.fulfilled, (state) => {
        state.disableLoading = false;
        state.enabled = false;
        state.hasBackupCodes = false;
        state.pendingSetup = false;
        state.backupCodes = [];
        state.qrCodeDataUrl = "";
        state.manualEntryKey = "";
      })
      .addCase(disableTwoFactor.rejected, (state, action) => {
        state.disableLoading = false;
        state.error = action.payload;
      })
      .addCase(regenerateBackupCodes.pending, (state) => {
        state.regenerateLoading = true;
        state.error = null;
      })
      .addCase(regenerateBackupCodes.fulfilled, (state, action) => {
        state.regenerateLoading = false;
        state.hasBackupCodes = true;
        state.backupCodes = backupCodesFromPayload(action.payload);
      })
      .addCase(regenerateBackupCodes.rejected, (state, action) => {
        state.regenerateLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearTwoFactorError, clearTwoFactorSetup, resetTwoFactor } =
  twoFactorApi.actions;

export default twoFactorApi.reducer;
